import type { ReactNode } from 'react';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import 'mantine-datatable/styles.css';
import { store, persistor, type RootState } from './store';
import { CurrencyProvider } from '@kss/currency';
import { ThemeSync } from '@kss/theme';
import { ToastProvider, CartProvider } from '@kss/ui';

/**
 * Reads the active user id from the auth slice and feeds it to CartProvider
 * so each customer keeps their own cart, and a sign-out doesn't leak the
 * previous user's items into the next session.
 */
function ScopedCart({ children }: { children: ReactNode }) {
  const userId = useSelector((s: RootState) => s.auth.user?.id ?? null);
  return <CartProvider userId={userId}>{children}</CartProvider>;
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <MantineProvider>
          <ToastProvider>
            <ScopedCart>
              <CurrencyProvider>
                <ThemeSync />
                {children}
              </CurrencyProvider>
            </ScopedCart>
          </ToastProvider>
        </MantineProvider>
      </PersistGate>
    </Provider>
  );
}
